import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { User, FileText, MessageCircle, ArrowLeft, Trash2, Pencil, Lock } from "lucide-react";
import { differenceInDays, format, parseISO, setYear } from "date-fns";
import { toast } from "sonner";
import type { Tables } from "@/integrations/supabase/types";
import MessageGenerator from "@/components/MessageGenerator";
import OccasionRow from "@/components/OccasionRow";
import AboutScreen from "@/components/AboutScreen";
import { OCCASION_TYPES, type OccasionType } from "@/lib/occasion-types";
import { PaywallModal } from "@/components/PaywallModal";

type Contact = Tables<"contacts">;

const RELATIONSHIP_EMOJI: Record<string, string> = {
  Partner: "💕", Parent: "🏠", Sibling: "👫", Child: "👶", Friend: "🤝", Colleague: "💼", Other: "✨",
};

function getDaysUntilBirthday(birthdayStr: string): number {
  const today = new Date();
  const birthday = parseISO(birthdayStr);
  let next = setYear(birthday, today.getFullYear());
  today.setHours(0, 0, 0, 0);
  next.setHours(0, 0, 0, 0);
  if (next < today) {
    next = setYear(birthday, today.getFullYear() + 1);
    next.setHours(0, 0, 0, 0);
  }
  return differenceInDays(next, today);
}

interface ContactDetailProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: Contact | null;
  occasions: OccasionData[];
  onEdit: (contact: Contact) => void;
  onDeleted: () => void;
}

interface OccasionData {
  id: string;
  occasion_type: OccasionType;
  label: string | null;
  date: string;
  is_recurring: boolean;
  converted_to_contact: boolean;
}

function hasEnrichmentData(contact: Contact): boolean {
  const c = contact as any;
  return !!(
    c.personality ||
    (c.loves && c.loves.length > 0) ||
    (c.dislikes && c.dislikes.length > 0) ||
    c.gift_ideas ||
    c.shared_memories ||
    c.notes
  );
}

const ContactDetail = ({ open, onOpenChange, contact, occasions, onEdit, onDeleted }: ContactDetailProps) => {
  const [showMessageGen, setShowMessageGen] = useState(false);
  const [messageOccasion, setMessageOccasion] = useState<{ type: OccasionType; label?: string; id?: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [localContact, setLocalContact] = useState<Contact | null>(null);
  const [paywallOpen, setPaywallOpen] = useState(false);

  const handleAboutSaved = useCallback(async () => {
    // Re-fetch the contact to get updated enrichment data
    if (!contact) return;
    const { data } = await supabase.from("contacts").select("*").eq("id", contact.id).single();
    if (data) setLocalContact(data);
    onDeleted(); // Also refresh parent list
  }, [contact, onDeleted]);

  const displayContact = localContact && contact && localContact.id === contact.id ? localContact : contact;
  const isLocked = !!(displayContact as any)?.locked;

  if (!displayContact) return null;

  const c = displayContact as any;

  const handleDelete = async () => {
    if (!confirm(`Remove ${displayContact.full_name} from your circle?`)) return;
    setDeleting(true);
    const { error } = await supabase.from("contacts").delete().eq("id", displayContact.id);
    setDeleting(false);
    if (error) {
      toast.error("Couldn't remove this person. Try again.");
    } else {
      toast.success(`${displayContact.full_name} removed from your circle.`);
      onOpenChange(false);
      onDeleted();
    }
  };

  const handleWriteMessage = (occasionType?: OccasionType, occasionLabel?: string, occasionId?: string) => {
    if (isLocked) {
      setPaywallOpen(true);
      return;
    }
    setMessageOccasion(occasionType ? { type: occasionType, label: occasionLabel, id: occasionId } : null);
    setShowMessageGen(true);
  };

  const handleEdit = () => {
    if (isLocked) {
      setPaywallOpen(true);
      return;
    }
    onOpenChange(false);
    setTimeout(() => onEdit(displayContact), 300);
  };

  const handleDismissNudge = async () => {
    await supabase
      .from("contacts")
      .update({ about_nudge_dismissed: true } as any)
      .eq("id", contact.id);
  };

  const firstName = displayContact.full_name.split(" ")[0];
  const enriched = hasEnrichmentData(displayContact);
  const nudgeDismissed = c.about_nudge_dismissed === true;

  if (showMessageGen) {
    return (
      <>
        <Sheet open={open} onOpenChange={(v) => { if (!v) { setShowMessageGen(false); setMessageOccasion(null); } onOpenChange(v); }}>
          <SheetContent className="overflow-y-auto bg-background sm:max-w-md">
            <div className="mb-4">
              <button
                onClick={() => { setShowMessageGen(false); setMessageOccasion(null); }}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" /> Back to {displayContact.full_name}
              </button>
            </div>
            <MessageGenerator
              contact={displayContact}
              occasionType={messageOccasion?.type}
              occasionLabel={messageOccasion?.label}
              occasionId={messageOccasion?.id}
              onOpenAbout={() => setAboutOpen(true)}
            />
          </SheetContent>
        </Sheet>
        <AboutScreen
          open={aboutOpen}
          onOpenChange={setAboutOpen}
          contact={displayContact}
          onSaved={handleAboutSaved}
        />
      </>
    );
  }

  // Build unified important dates list
  const allDates: Array<{
    key: string;
    occasionType: OccasionType;
    label: string | null;
    date: string;
    isRecurring: boolean;
    sortDays: number;
  }> = [];

  const birthdayDays = getDaysUntilBirthday(displayContact.birthday);
  allDates.push({
    key: "birthday",
    occasionType: "birthday",
    label: null,
    date: displayContact.birthday,
    isRecurring: true,
    sortDays: birthdayDays,
  });

  for (const occ of occasions) {
    if (occ.occasion_type === "birthday") continue;
    const days = getDaysUntilOccasionSafe(occ.date, occ.is_recurring);
    allDates.push({
      key: occ.id,
      occasionType: occ.occasion_type,
      label: occ.label,
      date: occ.date,
      isRecurring: occ.is_recurring,
      sortDays: days ?? 9999,
    });
  }

  allDates.sort((a, b) => a.sortDays - b.sortDays);

  const heroEntry = allDates[0];
  const heroDays = heroEntry.sortDays;
  const heroEmoji = OCCASION_TYPES[heroEntry.occasionType].emoji;

  const getHeroLabel = () => {
    const dayWord = heroDays === 1 ? "day" : "days";
    switch (heroEntry.occasionType) {
      case "bereavement":
        return `${dayWord} to remember`;
      case "new_baby":
        return `${dayWord} until their new arrival`;
      case "special_date":
        return `${dayWord} until ${heroEntry.label || "Special Date"}`;
      default:
        return `${dayWord} until ${OCCASION_TYPES[heroEntry.occasionType].label.toLowerCase()}`;
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="overflow-y-auto bg-background sm:max-w-md">
          <SheetHeader className="mb-6">
            <SheetTitle className="font-serif text-2xl">
              {displayContact.full_name} {RELATIONSHIP_EMOJI[displayContact.relationship] ?? "✨"}
            </SheetTitle>
          </SheetHeader>

          {/* Locked contact banner */}
          {isLocked && (
            <div className="mb-4 rounded-2xl border-l-[3px] border-l-accent bg-accent/5 p-4">
              <div className="flex items-start gap-2">
                <Lock className="mt-0.5 shrink-0 text-accent" size={14} />
                <div>
                  <p className="text-sm text-foreground">
                    This contact is locked. Upgrade to Circle Plus to edit, add occasions, or write messages.
                  </p>
                  <button
                    onClick={() => setPaywallOpen(true)}
                    className="mt-2 text-sm font-medium text-accent hover:underline"
                  >
                    Upgrade to Plus →
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Countdown hero */}
          <div className="mb-8 flex flex-col items-center rounded-2xl bg-accent/10 py-6">
            <span className="mb-2 text-2xl">{heroEmoji}</span>
            <span className="text-4xl font-bold text-foreground">{heroDays}</span>
            <span className="text-sm font-medium text-muted-foreground">
              {getHeroLabel()}
            </span>
          </div>

          {/* About section */}
          <div className="mb-6">
            {!enriched && !nudgeDismissed ? (
              /* State 1: Nudge card */
              <button
                onClick={() => setAboutOpen(true)}
                className="relative w-full rounded-2xl border-l-[3px] border-l-accent bg-card p-4 text-left"
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDismissNudge();
                    onDeleted();
                  }}
                  className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                >
                  <span className="text-sm">×</span>
                </button>
                <p className="pr-6 text-sm text-foreground">
                  Add more to get better messages 💛
                </p>
              </button>
            ) : !enriched && nudgeDismissed ? (
              /* State 2: Minimal link */
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-foreground">
                  About
                </p>
                <button
                  onClick={() => setAboutOpen(true)}
                  className="flex items-center gap-1 text-sm text-accent hover:underline"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Add more →
                </button>
              </div>
            ) : (
              /* State 3: Preview (read-only, tappable to open About) */
              <button
                onClick={() => setAboutOpen(true)}
                className="w-full text-left"
              >
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-foreground">
                  About
                </p>
                {c.personality && (
                  <p className="text-sm italic text-foreground truncate">{c.personality}</p>
                )}
                {c.loves && c.loves.length > 0 && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    Loves: {c.loves.join(" · ")}
                  </p>
                )}
                {c.dislikes && c.dislikes.length > 0 && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    Dislikes: {c.dislikes.join(" · ")}
                  </p>
                )}
              </button>
            )}
          </div>

          {/* Important dates */}
          {allDates.length > 0 && (
            <div className="mb-6">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-foreground">
                Important dates
              </p>
              <div className="divide-y divide-border">
                {allDates.map((d) => (
                  <OccasionRow
                    key={d.key}
                    occasionType={d.occasionType}
                    label={d.label}
                    date={d.date}
                    isRecurring={d.isRecurring}
                    showWriteLink
                    onWriteMessage={() => handleWriteMessage(d.occasionType, d.label ?? undefined, d.key !== "birthday" ? d.key : undefined)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Relationship */}
          <div className="mb-6">
            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-foreground">
              Relationship
            </p>
            <div className="flex items-center gap-3">
              <User className="h-4 w-4 text-accent" />
              <p className="text-sm text-foreground">{displayContact.relationship}</p>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-8 space-y-3">
            <Button
              className="w-full rounded-full"
              size="lg"
              onClick={() => handleWriteMessage(heroEntry.occasionType, heroEntry.label ?? undefined, heroEntry.key !== "birthday" ? heroEntry.key : undefined)}
            >
              <MessageCircle className="mr-2 h-4 w-4 shrink-0" />
              Write a message
            </Button>

            <Button
              variant="outline"
              className="w-full rounded-full"
              size="lg"
              onClick={handleEdit}
            >
              {isLocked && <Lock className="mr-2 h-3.5 w-3.5" />}
              Edit Details & About
            </Button>
            <p className="text-xs text-muted-foreground text-center">Including personality, loves, and notes</p>

            <Button
              variant="ghost"
              className="w-full rounded-full text-destructive hover:text-destructive"
              size="sm"
              onClick={handleDelete}
              disabled={deleting}
            >
              <Trash2 className="mr-2 h-3.5 w-3.5" />
              {deleting ? "Removing…" : "Remove from Circle"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <AboutScreen
        open={aboutOpen}
        onOpenChange={setAboutOpen}
        contact={displayContact}
        onSaved={handleAboutSaved}
      />

      <PaywallModal
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        title="This contact is locked"
        benefit="Upgrade to Circle Plus to unlock all your contacts, edit details, and write messages."
      />
    </>
  );
};

function getDaysUntilOccasionSafe(dateStr: string, isRecurring: boolean): number | null {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = parseISO(dateStr);

  if (!isRecurring) {
    const target = new Date(d);
    target.setHours(0, 0, 0, 0);
    const diff = differenceInDays(target, today);
    return diff < 0 ? null : diff;
  }

  let next = setYear(d, today.getFullYear());
  next.setHours(0, 0, 0, 0);
  if (next < today) {
    next = setYear(d, today.getFullYear() + 1);
    next.setHours(0, 0, 0, 0);
  }
  return differenceInDays(next, today);
}

export default ContactDetail;
